import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/auth';
import { HotelDetails } from '@/types/itinerary';
import { v4 as uuidv4 } from 'uuid';
import { supabase } from '@/integrations/supabase/client';

export const useHotelForm = (editHotelId?: string, tripId?: string) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState<HotelDetails>({
    name: '',
    address: '',
    checkIn: '',
    checkOut: '',
    confirmation: '',
    notes: '',
    price: '',
    phone: '',
    addedBy: '',
    roommates: [], // Initialize with empty array
    tripId: tripId || '' // Initialize tripId
  });
  
  const [isEditMode, setIsEditMode] = useState(false);

  useEffect(() => {
    if (editHotelId) {
      const savedHotels = JSON.parse(localStorage.getItem('manualHotels') || '[]');
      const hotelToEdit = savedHotels.find((hotel: any) => hotel.id === editHotelId);
      
      if (hotelToEdit) {
        console.log('Editing hotel with roommates:', hotelToEdit.roommates);
        setFormData({
          name: hotelToEdit.name || '',
          address: hotelToEdit.address || '',
          checkIn: hotelToEdit.checkIn || '',
          checkOut: hotelToEdit.checkOut || '',
          confirmation: hotelToEdit.confirmation || '',
          notes: hotelToEdit.notes || '',
          price: hotelToEdit.price ? String(hotelToEdit.price) : '',
          phone: hotelToEdit.phone || '',
          addedBy: hotelToEdit.addedBy || '',
          roommates: Array.isArray(hotelToEdit.roommates) ? hotelToEdit.roommates : [],
          tripId: hotelToEdit.tripId || tripId || '' // Keep the original tripId
        });
        setIsEditMode(true);
      }
    }
  }, [editHotelId, tripId]);

  useEffect(() => {
    if (!formData.addedBy) {
      const userName = user?.email?.split('@')[0] || localStorage.getItem('userName') || 'Anonymous';
      setFormData(prev => ({ ...prev, addedBy: userName }));
    }
  }, [user, formData.addedBy]);

  const handleFieldChange = (field: keyof HotelDetails, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addRoommate = (name: string) => {
    if (!name.trim()) return;
    
    console.log('Adding roommate:', name);
    const newRoommate = { id: uuidv4(), name: name.trim() };
    
    setFormData(prev => {
      const currentRoommates = Array.isArray(prev.roommates) ? prev.roommates : [];
      return {
        ...prev,
        roommates: [...currentRoommates, newRoommate]
      };
    });
  };

  const removeRoommate = (id: string) => {
    console.log('Removing roommate with id:', id);
    setFormData(prev => {
      const currentRoommates = Array.isArray(prev.roommates) ? prev.roommates : [];
      return {
        ...prev,
        roommates: currentRoommates.filter(roommate => roommate.id !== id)
      };
    });
  };

  const isFormValid = () => {
    return !!formData.name;
  };

  const saveHotel = () => {
    if (!isFormValid()) {
      toast.error('Please enter at least the hotel name');
      return;
    }

    if (!tripId) {
      toast.error('Unable to save hotel: No trip selected');
      return;
    }

    const existingHotels = JSON.parse(localStorage.getItem('manualHotels') || '[]');
    
    const roommatesArray = Array.isArray(formData.roommates) ? formData.roommates : [];
    
    const addToItinerary = async () => {
      try {
        await supabase.from('itinerary_events').insert([{
          id: uuidv4(),
          trip_id: tripId,
          title: `Check-in: ${formData.name}`,
          date: formData.checkIn,
          time: '15:00',
          location: formData.address,
          description: `Hotel Check-in\nConfirmation: ${formData.confirmation}\nPhone: ${formData.phone}\nNotes: ${formData.notes}`,
          created_by: user?.id || '',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          order_index: 0
        }]);

        await supabase.from('itinerary_events').insert([{
          id: uuidv4(),
          trip_id: tripId,
          title: `Check-out: ${formData.name}`,
          date: formData.checkOut,
          time: '11:00',
          location: formData.address,
          description: `Hotel Check-out\nConfirmation: ${formData.confirmation}\nPhone: ${formData.phone}`,
          created_by: user?.id || '',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          order_index: 0
        }]);
      } catch (error) {
        console.error('Error adding hotel to itinerary:', error);
        toast.error('Failed to add hotel to itinerary');
      }
    };

    if (isEditMode && editHotelId) {
      const updatedHotels = existingHotels.map((hotel: any) => 
        hotel.id === editHotelId 
          ? {
              ...hotel,
              name: formData.name,
              address: formData.address,
              price: parseFloat(formData.price as string) || 0,
              checkIn: formData.checkIn,
              checkOut: formData.checkOut,
              confirmation: formData.confirmation,
              notes: formData.notes,
              phone: formData.phone,
              addedBy: formData.addedBy,
              roommates: roommatesArray,
              tripId: formData.tripId || tripId
            }
          : hotel
      );
      
      localStorage.setItem('manualHotels', JSON.stringify(updatedHotels));
      addToItinerary();
      toast.success(`Updated ${formData.name} details`);
    } else {
      const newHotel = {
        id: `manual-${Date.now()}`,
        name: formData.name,
        address: formData.address,
        price: parseFloat(formData.price as string) || 0,
        rating: 0,
        checkIn: formData.checkIn,
        checkOut: formData.checkOut,
        confirmation: formData.confirmation,
        notes: formData.notes,
        phone: formData.phone,
        addedBy: formData.addedBy,
        roommates: roommatesArray,
        tripId: tripId
      };

      const updatedHotels = [...existingHotels, newHotel];
      localStorage.setItem('manualHotels', JSON.stringify(updatedHotels));
      addToItinerary();
      toast.success(`Added ${formData.name} to your trip`);
    }
    
    setFormData({
      name: '',
      address: '',
      checkIn: '',
      checkOut: '',
      confirmation: '',
      notes: '',
      price: '',
      phone: '',
      addedBy: formData.addedBy,
      roommates: [],
      tripId: tripId
    });
    
    setIsEditMode(false);

    window.dispatchEvent(new Event('storage'));
  };

  const cancelEdit = () => {
    setFormData({
      name: '',
      address: '',
      checkIn: '',
      checkOut: '',
      confirmation: '',
      notes: '',
      price: '',
      phone: '',
      addedBy: formData.addedBy,
      roommates: [],
      tripId: tripId
    });
    
    setIsEditMode(false);
  };

  return {
    formData,
    handleFieldChange,
    saveHotel,
    isFormValid: isFormValid(),
    addRoommate,
    removeRoommate,
    isEditMode,
    cancelEdit
  };
};
